// ═══════════════════════════════════════════════════════════════════════════════════
// AI Prodavac - LinkedIn Draft Funnel Component
// Shows LinkedIn draft lifecycle progression: Generated → Approved → Sent → Reply → Meeting
// ═══════════════════════════════════════════════════════════════════════════════════

import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ChevronDown, 
  FileText, 
  CheckCircle, 
  Send, 
  MessageSquare, 
  Calendar 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatUiNumber, formatUiPercent } from '@/lib/ui-format';

const formatRate = (value: number): string =>
  formatUiPercent(value / 100, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

interface LinkedInDraftFunnelProps {
  data?: {
    generated: number;
    approved: number;
    rejected: number;
    sent: number;
    gotReply: number;
    meetingsBooked: number;
    conversionRates: {
      approvalRate: number;
      sendRate: number;
      responseRate: number;
      meetingRate: number;
    };
  };
  isLoading?: boolean;
}

interface FunnelStage {
  label: string;
  count: number;
  icon: typeof FileText;
  dropOffRate: number;
  conversionRate?: number;
  color: 'info' | 'success' | 'warning' | 'danger';
}

const getConversionRateColor = (rate: number): 'success' | 'warning' | 'danger' => {
  if (rate >= 70) return 'success';
  if (rate >= 40) return 'warning';
  return 'danger';
};

const COLOR_CLASSES = {
  info: { 
    text: 'text-status-info', 
    bar: 'bg-status-info/20 border-l-status-info',
    icon: 'text-status-info',
    bg: 'bg-status-info/10'
  },
  success: { 
    text: 'text-status-success', 
    bar: 'bg-status-success/20 border-l-status-success',
    icon: 'text-status-success',
    bg: 'bg-status-success/10'
  },
  warning: { 
    text: 'text-status-warning', 
    bar: 'bg-status-warning/20 border-l-status-warning',
    icon: 'text-status-warning',
    bg: 'bg-status-warning/10'
  },
  danger: { 
    text: 'text-status-danger', 
    bar: 'bg-status-danger/20 border-l-status-danger',
    icon: 'text-status-danger',
    bg: 'bg-status-danger/10'
  },
} as const;

export function LinkedInDraftFunnel({ data, isLoading }: LinkedInDraftFunnelProps): JSX.Element {
  // Loading state
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">LinkedIn Draft Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded-lg bg-muted" />
                <div className="h-10 w-full animate-pulse rounded-lg bg-muted" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || data.generated === 0) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle className="text-lg">LinkedIn Draft Funnel</CardTitle>
          <p className="text-sm text-muted-foreground">
            Draft lifecycle progression and conversion rates
          </p>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            No draft data yet
          </p>
        </CardContent>
      </Card>
    );
  }

  const { 
    generated, 
    approved, 
    rejected, 
    sent, 
    gotReply, 
    meetingsBooked,
    conversionRates 
  } = data;

  // Build funnel stages
  const stages: FunnelStage[] = [
    {
      label: 'Generated',
      count: generated,
      icon: FileText,
      dropOffRate: 0,
      color: 'info',
    },
    {
      label: 'Approved',
      count: approved,
      icon: CheckCircle,
      dropOffRate: rejected,
      conversionRate: conversionRates.approvalRate,
      color: getConversionRateColor(conversionRates.approvalRate),
    },
    {
      label: 'Sent',
      count: sent,
      icon: Send,
      dropOffRate: approved - sent,
      conversionRate: conversionRates.sendRate,
      color: getConversionRateColor(conversionRates.sendRate),
    },
    {
      label: 'Reply',
      count: gotReply,
      icon: MessageSquare,
      dropOffRate: sent - gotReply,
      conversionRate: conversionRates.responseRate,
      color: getConversionRateColor(conversionRates.responseRate),
    },
    {
      label: 'Meeting',
      count: meetingsBooked,
      icon: Calendar,
      dropOffRate: gotReply - meetingsBooked,
      conversionRate: conversionRates.meetingRate,
      color: getConversionRateColor(conversionRates.meetingRate),
    },
  ];

  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="text-lg">LinkedIn Draft Funnel</CardTitle>
        <p className="text-sm text-muted-foreground">
          Draft lifecycle progression and conversion rates
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {stages.map((stage, index) => {
            const colorClasses = COLOR_CLASSES[stage.color];
            const width = (stage.count / maxCount) * 100;
            const Icon = stage.icon;
            
            return (
              <div key={stage.label}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', colorClasses.bg)}>
                        <Icon className={cn('w-4 h-4', colorClasses.icon)} />
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {stage.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={cn('text-sm font-bold', colorClasses.text)}>
                        {formatUiNumber(stage.count)}
                      </span>
                      {stage.conversionRate !== undefined && (
                        <span className={cn(
                          'text-xs font-semibold px-2 py-0.5 rounded-full',
                          stage.conversionRate >= 70 && 'bg-status-success/15 text-status-success',
                          stage.conversionRate >= 40 && stage.conversionRate < 70 && 'bg-status-warning/15 text-status-warning',
                          stage.conversionRate < 40 && 'bg-status-danger/15 text-status-danger'
                        )}>
                          {formatRate(stage.conversionRate)}
                        </span>
                      )}
                      {stage.dropOffRate > 0 && (
                        <span className="text-xs text-status-danger">
                          -{stage.dropOffRate}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="relative h-10 rounded-lg overflow-hidden bg-surface">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${width}%` }}
                      transition={{ 
                        duration: 0.8, 
                        delay: index * 0.1,
                        ease: "easeOut"
                      }}
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-lg border-l-4',
                        colorClasses.bar
                      )}
                    />
                  </div>
                </motion.div>

                {index < stages.length - 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    className="flex justify-center my-1.5"
                  >
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>

        {/* Conversion Rate Summary */}
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Conversion Rates
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Approval', rate: conversionRates.approvalRate },
              { label: 'Send', rate: conversionRates.sendRate },
              { label: 'Response', rate: conversionRates.responseRate },
              { label: 'Meeting', rate: conversionRates.meetingRate },
            ].map(({ label, rate }) => {
              const rateColor = getConversionRateColor(rate);
              const colorClasses = COLOR_CLASSES[rateColor];
              return (
                <div 
                  key={label}
                  className={cn(
                    'p-3 rounded-lg border',
                    rateColor === 'success' && 'bg-status-success/5 border-status-success/20',
                    rateColor === 'warning' && 'bg-status-warning/5 border-status-warning/20',
                    rateColor === 'danger' && 'bg-status-danger/5 border-status-danger/20'
                  )}
                >
                  <p className="text-xs text-muted-foreground mb-1">{label}</p>
                  <p className={cn('text-lg font-bold', colorClasses.text)}>
                    {formatRate(rate)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
